import React, { useState, useRef, useCallback } from 'react';
import {
  FaTimes,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaImage,
  FaClock,
  FaAlignLeft,
  FaBold,
  FaItalic,
  FaListUl,
  FaListOl,
  FaUser,
  FaFileAlt,
  FaHeading,
} from 'react-icons/fa';
import './AddEventModal.css';

import { API_BASE, authFetch, UPLOADS_BASE } from '@/services/api';

async function createEvent(formData: EventFormData): Promise<void> {
  const body = new FormData();
  body.append('title', formData.title);
  body.append('shortDescription', formData.shortDescription);
  body.append('host', formData.host);
  body.append('isOneDay', formData.isOneDay ? '1' : '0');
  body.append('startDate', formData.startDate);
  body.append('startTime', formData.startTime);
  body.append('endDate', formData.endDate);
  body.append('endTime', formData.endTime);
  body.append('location', formData.location);
  body.append('isFree', formData.isFree ? '1' : '0');
  body.append('fullDetails', formData.fullDetails);
  body.append('duration', formData.duration);
  body.append('requirements', formData.requirements);
  if (formData.featureImage) body.append('featureImage', formData.featureImage);
  if (formData.agendaFile) body.append('agendaFile', formData.agendaFile);
  const res = await authFetch(API_BASE + 'events/create', { method: 'POST', body });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.message || 'Failed to create event');
  if (!json.success) throw new Error(json?.message || 'Failed to create event');
}

async function updateEvent(id: number, formData: EventFormData): Promise<void> {
  const body = new FormData();
  body.append('id', String(id));
  body.append('title', formData.title);
  body.append('shortDescription', formData.shortDescription);
  body.append('host', formData.host);
  body.append('isOneDay', formData.isOneDay ? '1' : '0');
  body.append('startDate', formData.startDate);
  body.append('startTime', formData.startTime);
  body.append('endDate', formData.endDate);
  body.append('endTime', formData.endTime);
  body.append('location', formData.location);
  body.append('isFree', formData.isFree ? '1' : '0');
  body.append('fullDetails', formData.fullDetails);
  body.append('duration', formData.duration);
  body.append('requirements', formData.requirements);
  if (formData.featureImage) body.append('featureImage', formData.featureImage);
  if (formData.agendaFile) body.append('agendaFile', formData.agendaFile);
  const res = await authFetch(API_BASE + 'events/update', { method: 'POST', body });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.message || 'Failed to update event');
  if (!json.success) throw new Error(json?.message || 'Failed to update event');
}

export interface EventRecordForEdit {
  id: number;
  title: string;
  short_description: string;
  host: string;
  is_one_day: number;
  start_date: string;
  start_time: string;
  end_date: string | null;
  end_time: string | null;
  location: string;
  is_free: number;
  full_details: string | null;
  feature_image: string | null;
  agenda_file: string | null;
  duration: string | null;
  requirements: string | null;
}

export interface EventFormData {
  title: string;
  shortDescription: string;
  host: string;
  isOneDay: boolean;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  location: string;
  isFree: boolean;
  fullDetails: string;
  featureImage: File | null;
  featureImagePreview: string;
  agendaFile: File | null;
  agendaFileName: string;
  duration: string;
  requirements: string;
}

const initialForm: EventFormData = {
  title: '',
  shortDescription: '',
  host: '',
  isOneDay: true,
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  location: '',
  isFree: true,
  fullDetails: '',
  featureImage: null,
  featureImagePreview: '',
  agendaFile: null,
  agendaFileName: '',
  duration: '',
  requirements: '',
};

function getUploadUrl(path: string | null): string {
  if (!path) return '';
  return path.startsWith('http') ? path : UPLOADS_BASE + path.replace(/^\//, '');
}

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data?: EventFormData) => void;
  editingEventId?: number | null;
  initialEvent?: EventRecordForEdit | null;
}

export const AddEventModal: React.FC<AddEventModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingEventId,
  initialEvent,
}) => {
  const isEditMode = editingEventId != null && editingEventId > 0 && initialEvent != null;
  const [form, setForm] = useState<EventFormData>(initialForm);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const richTextRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isOpen && isEditMode && initialEvent) {
      const t = initialEvent.start_time?.slice(0, 5) ?? '';
      const endT = initialEvent.end_time?.slice(0, 5) ?? '';
      setForm({
        title: initialEvent.title,
        shortDescription: initialEvent.short_description,
        host: initialEvent.host,
        isOneDay: !!initialEvent.is_one_day,
        startDate: initialEvent.start_date,
        startTime: t,
        endDate: initialEvent.end_date ?? '',
        endTime: endT,
        location: initialEvent.location,
        isFree: !!initialEvent.is_free,
        fullDetails: initialEvent.full_details ?? '',
        featureImage: null,
        featureImagePreview: initialEvent.feature_image ? getUploadUrl(initialEvent.feature_image) : '',
        agendaFile: null,
        agendaFileName: '',
        duration: initialEvent.duration ?? '',
        requirements: initialEvent.requirements ?? '',
      });
      setTimeout(() => {
        if (richTextRef.current) richTextRef.current.innerHTML = initialEvent.full_details ?? '';
      }, 0);
    } else if (isOpen && !isEditMode) {
      setForm(initialForm);
      setTimeout(() => {
        if (richTextRef.current) richTextRef.current.innerHTML = '';
      }, 0);
    }
  }, [isOpen, isEditMode, initialEvent]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      if (name === 'featureImage') {
        const file = (e.target as HTMLInputElement).files?.[0];
        setForm((prev) => ({
          ...prev,
          featureImage: file ?? null,
          featureImagePreview: file ? URL.createObjectURL(file) : '',
        }));
        return;
      }
      if (name === 'agendaFile') {
        const file = (e.target as HTMLInputElement).files?.[0];
        setForm((prev) => ({
          ...prev,
          agendaFile: file ?? null,
          agendaFileName: file ? file.name : '',
        }));
        return;
      }
      if (name === 'isFree') {
        setForm((prev) => ({ ...prev, isFree: value === 'free' }));
        return;
      }
      if (name === 'eventSpan') {
        setForm((prev) => ({ ...prev, isOneDay: value === 'one' }));
        return;
      }
      setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    },
    []
  );

  const handleRichTextInput = useCallback(() => {
    const html = richTextRef.current?.innerHTML ?? '';
    setForm((prev) => ({ ...prev, fullDetails: html }));
  }, []);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    richTextRef.current?.focus();
    handleRichTextInput();
  }, [handleRichTextInput]);

  const handleClose = useCallback(() => {
    if (form.featureImagePreview) {
      URL.revokeObjectURL(form.featureImagePreview);
    }
    setForm(initialForm);
    setSubmitMessage(null);
    onClose();
  }, [onClose, form.featureImagePreview]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitMessage(null);

      if (!form.title.trim()) {
        setSubmitMessage({ type: 'error', text: 'Please enter the event title.' });
        return;
      }
      if (!form.shortDescription.trim()) {
        setSubmitMessage({ type: 'error', text: 'Please enter a short description.' });
        return;
      }
      if (!form.host.trim()) {
        setSubmitMessage({ type: 'error', text: 'Please enter the event host.' });
        return;
      }
      if (!form.startDate) {
        setSubmitMessage({ type: 'error', text: 'Please select the start date.' });
        return;
      }
      if (!form.startTime) {
        setSubmitMessage({ type: 'error', text: 'Please select the start time.' });
        return;
      }
      if (!form.isOneDay && !form.endDate) {
        setSubmitMessage({ type: 'error', text: 'Please select the end date for multi-day events.' });
        return;
      }
      if (!form.isOneDay && !form.endTime) {
        setSubmitMessage({ type: 'error', text: 'Please select the end time for multi-day events.' });
        return;
      }
      if (!form.location.trim()) {
        setSubmitMessage({ type: 'error', text: 'Please enter the event location.' });
        return;
      }

      setIsSubmitting(true);
      setSubmitMessage(null);

      const fullDetails = richTextRef.current?.innerHTML ?? form.fullDetails;
      const dataToSubmit: EventFormData = { ...form, fullDetails };

      try {
        if (isEditMode && editingEventId) {
          await updateEvent(editingEventId, dataToSubmit);
          setSubmitMessage({ type: 'success', text: 'Event updated successfully.' });
        } else {
          await createEvent(dataToSubmit);
          setSubmitMessage({ type: 'success', text: 'Event created successfully.' });
        }
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 600);
      } catch (err) {
        setSubmitMessage({
          type: 'error',
          text: err instanceof Error ? err.message : (isEditMode ? 'Failed to update event.' : 'Failed to create event.'),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, onSuccess, handleClose, isEditMode, editingEventId]
  );

  if (!isOpen) return null;

  return (
    <div
      className="kf-modal__overlay add-event-modal__overlay"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-event-modal-title"
    >
      <div className="kf-modal add-event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kf-modal__header">
          <h2 id="add-event-modal-title">{isEditMode ? 'Edit Event' : 'Add New Event'}</h2>
          <button
            type="button"
            className="kf-modal__close"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="kf-modal__form">
          <div className="kf-modal__body add-event-modal__body">
            {submitMessage && (
              <div
                className={`kf-modal__message kf-modal__message--${submitMessage.type}`}
                role="alert"
              >
                {submitMessage.text}
              </div>
            )}

            <div className="kf-modal__row">
              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-title">
                  Title <span>*</span>
                </label>
                <input
                  id="event-title"
                  name="title"
                  type="text"
                  placeholder="Event title"
                  value={form.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-feature-image">
                  <FaImage className="kf-modal__label-icon" /> Feature image
                </label>
                <div className="add-event-modal__image-upload">
                  <input
                    id="event-feature-image"
                    name="featureImage"
                    type="file"
                    accept="image/*"
                    onChange={handleChange}
                    className="add-event-modal__file-input"
                  />
                  {form.featureImagePreview ? (
                    <div className="add-event-modal__image-preview">
                      <img src={form.featureImagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="add-event-modal__remove-image"
                        onClick={() => {
                          setForm((prev) => {
                            if (prev.featureImagePreview) URL.revokeObjectURL(prev.featureImagePreview);
                            return { ...prev, featureImage: null, featureImagePreview: '' };
                          });
                        }}
                        aria-label="Remove image"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="event-feature-image" className="add-event-modal__upload-placeholder">
                      <FaImage />
                      <span>Choose image</span>
                    </label>
                  )}
                </div>
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-short-description">
                  Short description <span>*</span>
                </label>
                <textarea
                  id="event-short-description"
                  name="shortDescription"
                  placeholder="Brief summary for listings and cards"
                  value={form.shortDescription}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-host">
                  <FaUser className="kf-modal__label-icon" /> Host <span>*</span>
                </label>
                <input
                  id="event-host"
                  name="host"
                  type="text"
                  placeholder="e.g. Parish name, Friary, Organizer"
                  value={form.host}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <span className="kf-modal__section-title">Event span</span>
                <div className="kf-modal__radio-group">
                  <label className="kf-modal__radio-label">
                    <input
                      type="radio"
                      name="eventSpan"
                      value="one"
                      checked={form.isOneDay}
                      onChange={handleChange}
                    />
                    One day
                  </label>
                  <label className="kf-modal__radio-label">
                    <input
                      type="radio"
                      name="eventSpan"
                      value="multi"
                      checked={!form.isOneDay}
                      onChange={handleChange}
                    />
                    More than one day
                  </label>
                </div>
              </div>

              <div className="kf-modal__group">
                <label htmlFor="event-start-date">
                  <FaCalendarAlt className="kf-modal__label-icon" /> Start date <span>*</span>
                </label>
                <input
                  id="event-start-date"
                  name="startDate"
                  type="date"
                  value={form.startDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="kf-modal__group">
                <label htmlFor="event-start-time">
                  <FaClock className="kf-modal__label-icon" /> Start time <span>*</span>
                </label>
                <input
                  id="event-start-time"
                  name="startTime"
                  type="time"
                  value={form.startTime}
                  onChange={handleChange}
                  required
                />
              </div>

              {!form.isOneDay && (
                <>
                  <div className="kf-modal__group">
                    <label htmlFor="event-end-date">
                      <FaCalendarAlt className="kf-modal__label-icon" /> End date <span>*</span>
                    </label>
                    <input
                      id="event-end-date"
                      name="endDate"
                      type="date"
                      value={form.endDate}
                      onChange={handleChange}
                      min={form.startDate}
                      required={!form.isOneDay}
                    />
                  </div>
                  <div className="kf-modal__group">
                    <label htmlFor="event-end-time">
                      <FaClock className="kf-modal__label-icon" /> End time <span>*</span>
                    </label>
                    <input
                      id="event-end-time"
                      name="endTime"
                      type="time"
                      value={form.endTime}
                      onChange={handleChange}
                      required={!form.isOneDay}
                    />
                  </div>
                </>
              )}

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-duration">
                  <FaClock className="kf-modal__label-icon" /> Duration (optional)
                </label>
                <input
                  id="event-duration"
                  name="duration"
                  type="text"
                  placeholder="e.g. 2 hours, Full day"
                  value={form.duration}
                  onChange={handleChange}
                />
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-location">
                  <FaMapMarkerAlt className="kf-modal__label-icon" /> Location <span>*</span>
                </label>
                <input
                  id="event-location"
                  name="location"
                  type="text"
                  placeholder="Venue or address"
                  value={form.location}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <span className="kf-modal__section-title">Admission</span>
                <div className="kf-modal__radio-group">
                  <label className="kf-modal__radio-label">
                    <input
                      type="radio"
                      name="isFree"
                      value="free"
                      checked={form.isFree}
                      onChange={handleChange}
                    />
                    Free
                  </label>
                  <label className="kf-modal__radio-label">
                    <input
                      type="radio"
                      name="isFree"
                      value="paid"
                      checked={!form.isFree}
                      onChange={handleChange}
                    />
                    Paid
                  </label>
                </div>
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-agenda">
                  <FaFileAlt className="kf-modal__label-icon" /> Event agenda (optional)
                </label>
                <div className="add-event-modal__agenda-upload">
                  <input
                    id="event-agenda"
                    name="agendaFile"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleChange}
                    className="add-event-modal__file-input"
                  />
                  {form.agendaFileName ? (
                    <div className="add-event-modal__agenda-file">
                      <FaFileAlt />
                      <span className="add-event-modal__agenda-filename">{form.agendaFileName}</span>
                      <button
                        type="button"
                        className="add-event-modal__remove-agenda"
                        onClick={() => setForm((prev) => ({ ...prev, agendaFile: null, agendaFileName: '' }))}
                        aria-label="Remove agenda file"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="event-agenda" className="add-event-modal__upload-placeholder add-event-modal__upload-placeholder--agenda">
                      <FaFileAlt />
                      <span>Upload agenda (PDF or Word)</span>
                    </label>
                  )}
                </div>
              </div>

              <div className="kf-modal__group add-event-modal__rich-text-wrap" style={{ gridColumn: '1 / -1' }}>
                <span className="kf-modal__section-title">
                  <FaAlignLeft /> Full event details (rich text)
                </span>
                <div className="add-event-modal__rich-toolbar">
                  <div className="add-event-modal__rich-toolbar-heading">
                    <FaHeading />
                    <select
                      className="add-event-modal__heading-select"
                      title="Paragraph style"
                      aria-label="Paragraph style"
                      defaultValue=""
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          execCommand('formatBlock', value);
                          e.target.value = '';
                        }
                      }}
                    >
                      <option value="">Heading</option>
                      <option value="p">Paragraph</option>
                      <option value="h2">Heading 2</option>
                      <option value="h3">Heading 3</option>
                    </select>
                  </div>
                  <button type="button" onClick={() => execCommand('bold')} title="Bold" aria-label="Bold">
                    <FaBold />
                  </button>
                  <button type="button" onClick={() => execCommand('italic')} title="Italic" aria-label="Italic">
                    <FaItalic />
                  </button>
                  <button type="button" onClick={() => execCommand('insertUnorderedList')} title="Bullet list" aria-label="Bullet list">
                    <FaListUl />
                  </button>
                  <button type="button" onClick={() => execCommand('insertOrderedList')} title="Numbered list" aria-label="Numbered list">
                    <FaListOl />
                  </button>
                </div>
                <div
                  ref={richTextRef}
                  className="add-event-modal__rich-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleRichTextInput}
                  data-placeholder="Write full event description, schedule, and other details…"
                />
              </div>

              <div className="kf-modal__group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="event-requirements">Requirements (optional)</label>
                <textarea
                  id="event-requirements"
                  name="requirements"
                  placeholder="e.g. Registration required, bring own materials"
                  value={form.requirements}
                  onChange={handleChange}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="kf-modal__actions">
            <button type="button" className="kf-modal__btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="kf-modal__btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : isEditMode ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
